import { Info } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface MkbBundleWarningBannerProps {
	/** Whether the quotes list is currently polling/loading */
	isPolling?: boolean;
}

/**
 * Info banner displayed when the current policy is recognized as an MKB
 * Verzuim-Ontzorgverzekering (SME absence-relief bundle).
 *
 * The funnel still produces Ziekteverzuim Conventioneel quotes from any
 * sick_leave_short_term coverage extracted from the bundle, but the user
 * needs to know that the proposed product is different from the bundle
 * and that the actual coverage and service may diverge.
 */
const MkbBundleWarningBanner = ({
	isPolling = false
}: MkbBundleWarningBannerProps): React.JSX.Element => {
	const { t } = useTranslation();

	return (
		<div
			className={cn(
				'flex p-0 items-start bg-primary/10 border border-primary/50 rounded-lg',
				isPolling && 'pointer-events-none saturate-[0.25] opacity-50'
			)}
			role="alert"
			data-testid="mkb-bundle-warning-banner"
		>
			<div className="grow bg-white/50 p-4 rounded-lg">
				<div className="flex items-start gap-3">
					<Info className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
					<div className="grow">
						<h6 className="text-base font-bold text-primary mb-2">
							{t('quotes.mkbBundleWarning.title')}
						</h6>
						<p className="text-foreground mb-0">{t('quotes.mkbBundleWarning.description')}</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MkbBundleWarningBanner;
